"use client"

import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"
import { useTranslation } from "react-i18next"
import FormattedPrice from "@src/components/FormattedPrice"
import UpcomingCountdown from "./UpcomingCountdown"
import { resolveUpcomingDealDisplay } from "@src/utils/upcoming-deal-resolver"

export interface UpcomingSalePanelProps {
  variant?: "card-overlay" | "card-inline" | "popup" | "detail"
  compact?: boolean
  cardDetails?: boolean
  isPopup?: boolean
  originalPrice: number
  futurePrice: number
  discountPercent: number
  from: string
  to?: string | null
  locale?: string
  eyebrow?: string
  autoAppliedLabel?: string
}

const UpcomingSalePanel = ({
  variant,
  compact = false,
  cardDetails = false,
  isPopup = false,
  originalPrice,
  futurePrice,
  discountPercent,
  from,
  to,
  locale,
  eyebrow,
  autoAppliedLabel
}: UpcomingSalePanelProps) => {
  const { t, i18n } = useTranslation()
  const isEn = i18n?.language?.startsWith("en")
  const activeLocale = locale || (isEn ? "en-US" : "vi-VN")
  const activeEyebrow = eyebrow || t("upcoming_price_eyebrow", { defaultValue: "Giá khi mở sale" })
  const activeAutoApplied = autoAppliedLabel || t("upcoming_auto_applied", { defaultValue: "Giá ưu đãi tự động áp dụng khi bắt đầu" })

  const start = new Date(from)
  const end = to ? new Date(to) : null
  if (Number.isNaN(start.getTime())) return null

  const activeVariant: "card-overlay" | "card-inline" | "popup" | "detail" =
    variant ??
    (cardDetails
      ? "card-inline"
      : isPopup
        ? "popup"
        : compact
          ? "card-overlay"
          : "detail")

  const deal = resolveUpcomingDealDisplay({
    discountPercent,
    futurePrice,
    originalPrice
  })

  const isPercentDeal = deal.type === "percent"
  const salePrice = deal.type === "price" ? deal.price : null
  const detailSalePrice =
    deal.type === "percent" && originalPrice > 0
      ? Math.round(originalPrice * (1 - deal.percent / 100))
      : salePrice

  const conditions = `${activeAutoApplied}. ${t("upcoming_conditions_hint", { defaultValue: "Mua trước thời điểm bắt đầu áp dụng giá hiện tại." })}${
    end && !Number.isNaN(end.getTime())
      ? ` ${t("ends_at", { time: end.toLocaleString(activeLocale), defaultValue: `Kết thúc ${end.toLocaleString(activeLocale)}` })}.`
      : ""
  }`

  // 1. Mobile product card - inline below product name
  if (activeVariant === "card-inline") {
    return (
      <Box
        component="section"
        aria-label={t("upcoming_price_title", { defaultValue: "Giá ưu đãi sắp áp dụng" })}
        sx={{ minWidth: 0, width: "100%", mt: 0.5 }}
      >
        <Stack direction="row" alignItems="baseline" gap={0.75} flexWrap="wrap">
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 800,
              color: "#B42D37",
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {isPercentDeal
              ? t("discount_percent_label", { percent: discountPercent, defaultValue: `Giảm ${discountPercent}%` })
              : salePrice !== null
                ? <FormattedPrice price={salePrice} />
                : t("upcoming_sale_badge", { defaultValue: "Sắp sale" })}
          </Typography>
          {originalPrice > 0 && (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                color: "#8A8380",
                textDecoration: "line-through",
                fontVariantNumeric: "tabular-nums"
              }}
            >
              <FormattedPrice price={originalPrice} />
            </Typography>
          )}
        </Stack>

        <Box
          sx={{
            mt: 0.75,
            px: 1,
            py: 0.45,
            borderRadius: "6px",
            bgcolor: "rgba(255, 244, 245, 0.3)",
            border: "1px solid rgba(252, 217, 222, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5
          }}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Box
              component="svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B42D37"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: "#8E2B34",
                lineHeight: 1,
                whiteSpace: "nowrap"
              }}
            >
              {t("sale_open_after", { defaultValue: "Mở bán sau" })}
            </Typography>
          </Stack>
          <UpcomingCountdown targetDate={from} variant="compact" size="xs" />
        </Box>
      </Box>
    )
  }

  // 2. Desktop product card - compact overlay at bottom of product image
  if (activeVariant === "card-overlay") {
    return (
      <Tooltip title={conditions} arrow enterTouchDelay={0}>
        <Box
          component="section"
          tabIndex={0}
          data-testid="upcoming-sale-panel"
          aria-label={t("upcoming_price_title", { defaultValue: "Giá ưu đãi sắp áp dụng" })}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: { xs: "auto", md: 48 },
            minHeight: { xs: 42, md: 48 },
            boxSizing: "border-box",
            px: { xs: 0.75, sm: 1, md: 1.25 },
            py: { xs: 0.4, md: 0.5 },
            borderRadius: { xs: "8px", md: "10px" },
            bgcolor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid rgba(255, 255, 255, 0.45)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            minWidth: 0,
            width: "100%",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              boxShadow: "0 4px 14px rgba(180, 45, 55, 0.1)"
            },
            "&:focus-visible": {
              outline: "2px solid #D8042C",
              outlineOffset: 2
            }
          }}
        >
          {/* Mobile view: 2 stacked rows (Row 1: Giá sắp mở bán + Price + Tag; Row 2: Mở bán sau + Timer) */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              justifyContent: "center",
              gap: "2px",
              width: "100%",
              minWidth: 0
            }}
          >
            {/* Row 1: SẮP SALE + Deal Value (either % or price) */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={0.5}
              width="100%"
              minWidth={0}
              sx={{ pb: "3px" }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                flexShrink={0}
                sx={{ lineHeight: 1 }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#B42D37",
                    boxShadow: "0 0 0 2px rgba(180, 45, 55, 0.18)",
                    display: "inline-block",
                    flexShrink: 0
                  }}
                />
                <Typography
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: { xs: 8.5, sm: 9 },
                    fontWeight: 700,
                    lineHeight: 1,
                    color: "#8E2B34",
                    textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                    letterSpacing: "0.02em"
                  }}
                >
                  {t("upcoming_sale_badge", { defaultValue: "Sắp sale" })}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 12.5 },
                  lineHeight: 1.15,
                  color: "#B42D37",
                  textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {isPercentDeal
                  ? `-${discountPercent}%`
                  : salePrice !== null
                    ? <FormattedPrice price={salePrice} />
                    : t("upcoming_sale_badge", { defaultValue: "Sắp sale" })}
              </Typography>
            </Stack>

            {/* Row 2: Mở bán sau + Countdown */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={0.5}
              width="100%"
              sx={{
                pt: "3px",
                borderTop: "1px solid rgba(180, 45, 55, 0.12)"
              }}
            >
              <Typography
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: { xs: 8, sm: 8.5 },
                  fontWeight: 600,
                  lineHeight: 1,
                  color: "#5A4E4C",
                  textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                  whiteSpace: "nowrap"
                }}
              >
                {t("sale_starts_in", { defaultValue: "Mở bán sau" })}
              </Typography>
              <UpcomingCountdown targetDate={from} variant="compact" size="xs" />
            </Stack>
          </Box>

          {/* Desktop view: 2 columns side-by-side */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: "100%",
              minWidth: 0
            }}
          >
            {/* Left: Upcoming Price or Discount */}
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: "#8E2B34",
                  textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em"
                }}
              >
                {isPercentDeal ? t("upcoming_sale_title", { defaultValue: "Ưu đãi sắp mở" }) : t("upcoming_price_title", { defaultValue: "Giá sắp mở bán" })}
              </Typography>
              <Typography
                sx={{
                  mt: 0.15,
                  fontSize: 14.5,
                  lineHeight: 1.15,
                  color: "#B42D37",
                  textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {isPercentDeal
                  ? t("discount_percent_label", { percent: discountPercent, defaultValue: `Giảm ${discountPercent}%` })
                  : salePrice !== null
                    ? <FormattedPrice price={salePrice} />
                    : t("upcoming_sale_badge", { defaultValue: "Sắp sale" })}
              </Typography>
            </Box>

            {/* Vertical Divider */}
            <Box
              sx={{
                width: "1px",
                height: 24,
                bgcolor: "rgba(180, 45, 55, 0.12)",
                flexShrink: 0,
                mx: 0.5
              }}
            />

            {/* Right: Countdown */}
            <Box sx={{ minWidth: 0, flexShrink: 0, textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  lineHeight: 1.15,
                  color: "#5A4E4C",
                  textShadow: "0 1px 2px rgba(255, 255, 255, 0.4)",
                  whiteSpace: "nowrap"
                }}
              >
                {t("sale_open_after", { defaultValue: "Mở bán sau" })}
              </Typography>
              <Box sx={{ mt: 0.15 }}>
                <UpcomingCountdown targetDate={from} variant="compact" size="sm" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Tooltip>
    )
  }

  // 3. Popup product modal - embedded in product info area
  if (activeVariant === "popup") {
    return (
      <Box
        component="section"
        data-testid="upcoming-sale-popup-panel"
        aria-label={t("upcoming_price_title", { defaultValue: "Giá ưu đãi sắp áp dụng" })}
        sx={{
          width: "100%",
          borderRadius: "12px",
          background: "linear-gradient(135deg, rgba(255, 246, 247, 0.25) 0%, rgba(255, 255, 255, 0.25) 100%)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          border: "1px solid rgba(243, 207, 212, 0.6)",
          boxShadow: "0 2px 8px rgba(180, 45, 55, 0.04)",
          p: { xs: 1.25, sm: 1.5 },
          my: 1.25,
          boxSizing: "border-box"
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: "#B42D37",
                boxShadow: "0 0 0 3px rgba(180, 45, 55, 0.15)",
                flexShrink: 0
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 700,
                color: "#8E2B34",
                textTransform: "uppercase",
                letterSpacing: "0.04em"
              }}
            >
              {isPercentDeal ? t("upcoming_sale_title_full", { defaultValue: "Ưu đãi sắp mở bán" }) : t("upcoming_price_title", { defaultValue: "Giá sắp mở bán" })}
            </Typography>
            <Tooltip title={conditions} arrow enterTouchDelay={0}>
              <Box
                component="button"
                type="button"
                aria-label={t("upcoming_conditions_hint", { defaultValue: "Điều kiện áp dụng ưu đãi" })}
                sx={{
                  p: 0,
                  width: 20,
                  height: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "transparent",
                  border: 0,
                  color: "#888",
                  cursor: "help",
                  borderRadius: "50%",
                  "&:hover": { color: "#B42D37" }
                }}
              >
                <Box
                  component="svg"
                  width="13"
                  height="13"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10 9v5M10 6v1" stroke="currentColor" strokeWidth="1.5" />
                </Box>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={{ xs: 1.25, sm: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#B42D37",
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 800,
                lineHeight: 1.15,
                fontVariantNumeric: "tabular-nums"
              }}
            >
              {isPercentDeal
                ? t("discount_percent_label", { percent: discountPercent, defaultValue: `Giảm ${discountPercent}%` })
                : salePrice !== null
                  ? <FormattedPrice price={salePrice} />
                  : t("waiting_update", { defaultValue: "Chờ cập nhật" })}
            </Typography>
            {originalPrice > 0 && (
              <Stack direction="row" alignItems="baseline" gap={0.6} mt={0.3}>
                <Typography component="span" sx={{ color: "#7A6F6D", fontSize: 11.5 }}>
                  {t("current_price_label", { defaultValue: "Giá hiện tại:" })}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: "#7A6F6D",
                    fontSize: 12,
                    textDecoration: "line-through",
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  <FormattedPrice price={originalPrice} />
                </Typography>
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              bgcolor: "transparent",
              border: "1px solid #ECD4D7",
              borderRadius: "8px",
              px: { xs: 1.25, sm: 1.5 },
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexShrink: 0
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                color: "#7A6F6D",
                textTransform: "uppercase",
                letterSpacing: "0.02em"
              }}
            >
              {t("sale_open_after", { defaultValue: "Mở bán sau" })}
            </Typography>
            <UpcomingCountdown targetDate={from} variant="blocks" size="sm" />
          </Box>
        </Stack>
      </Box>
    )
  }

  // 4. Product detail page - overlay on bottom of product gallery
  return (
    <Box
      component="section"
      data-testid="upcoming-sale-panel"
      aria-label={t("upcoming_price_title", { defaultValue: "Giá ưu đãi sắp áp dụng" })}
      sx={{
        border: "1px solid rgba(255, 255, 255, 0.45)",
        borderRadius: { xs: "12px", sm: "16px" },
        overflow: "hidden",
        background:
          "linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 248, 249, 0.2) 100%)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
        minWidth: 0,
        width: "100%",
        p: { xs: 1.25, sm: 2 }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ mb: { xs: 0.75, sm: 1.25 } }}
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box
            component="span"
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#B42D37",
              boxShadow: "0 0 0 4px rgba(180, 45, 55, 0.15)",
              flexShrink: 0
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 13.5 },
              fontWeight: 700,
              color: "#71363A",
              textTransform: "uppercase",
              letterSpacing: "0.03em"
            }}
          >
            {isPercentDeal ? t("upcoming_sale_title_full", { defaultValue: "Ưu đãi khi mở sale" }) : activeEyebrow}
          </Typography>
          <Tooltip title={conditions} arrow enterTouchDelay={0}>
            <Box
              component="button"
              type="button"
              aria-label={t("upcoming_conditions_hint", { defaultValue: "Điều kiện áp dụng ưu đãi" })}
              sx={{
                p: 0,
                width: 22,
                height: 22,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "transparent",
                border: 0,
                color: "#777",
                cursor: "help",
                borderRadius: "50%",
                "&:hover": { color: "#B42D37" }
              }}
            >
              <Box
                component="svg"
                width="13"
                height="13"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10 9v5M10 6v1" stroke="currentColor" strokeWidth="1.5" />
              </Box>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={{ xs: 1, sm: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#B42D37",
              fontSize: { xs: 22, sm: 28 },
              fontWeight: 800,
              lineHeight: 1.15,
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {detailSalePrice !== null
              ? <FormattedPrice price={detailSalePrice} />
              : t("waiting_update", { defaultValue: "Chờ cập nhật" })}
          </Typography>
          {isPercentDeal && (
            <Typography
              sx={{
                mt: 0.35,
                color: "#B42D37",
                fontSize: { xs: 12, sm: 13 },
                fontWeight: 700,
                lineHeight: 1.2
              }}
            >
              {t("discount_percent_label", {
                percent: discountPercent,
                defaultValue: `Giảm ${discountPercent}%`
              })}
            </Typography>
          )}
          {originalPrice > 0 && (
            <Stack direction="row" alignItems="baseline" gap={0.75} mt={0.25}>
              <Typography component="span" sx={{ color: "#817774", fontSize: 11.5 }}>
                {t("current_price_label", { defaultValue: "Giá hiện tại:" })}
              </Typography>
              <Typography
                component="span"
                aria-label={t("current_price_label", { defaultValue: "Giá hiện tại" })}
                sx={{
                  color: "#817774",
                  fontSize: { xs: 12, sm: 13 },
                  textDecoration: "line-through",
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                <FormattedPrice price={originalPrice} />
              </Typography>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            pl: { xs: 0, sm: 2 },
            pt: { xs: 0.75, sm: 0 },
            borderLeft: { xs: 0, sm: "1px solid #EAD5D8" },
            borderTop: { xs: "1px solid #EAD5D8", sm: 0 },
            flexShrink: 0
          }}
        >
          <UpcomingCountdown
            targetDate={from}
            label={t("sale_open_after", { defaultValue: "Mở bán sau" })}
            variant="blocks"
            size="md"
          />
        </Box>
      </Stack>
    </Box>
  )
}

export default UpcomingSalePanel
